import BaseController from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type Dialog from "sap/m/Dialog";
import type { FileUploader$ChangeEvent } from "sap/ui/unified/FileUploader";
import type { ResponseError } from "../types/Errors";
import type { PromoBannerListItem } from "./Promotions.controller";
import { problemMessage } from "../util/ProblemDetails";

interface PromoPublicationWindowDto {
	id: string;
	openedAt: string;
	openedBy: string;
	openedByName: string | null;
	closedAt: string | null;
	closedBy: string | null;
	closedByName: string | null;
	closeReason: string | null;
}

/** One day of the Effectiveness chart: both kinds pivoted onto a single row, scaled to the peak. */
interface PromoDailyChartRow {
	day: string;
	impressions: number;
	clicks: number;
	impressionsPercent: number;
	clicksPercent: number;
	distinctEnvironments: number;
}

interface PromoDailyStatDto {
	day: string;
	kind: string;
	count: number;
	distinctEnvironments: number;
}

interface PromoBannerDetailDto {
	summary: PromoBannerListItem;
	subtitle: string | null;
	mediaAltText: string;
	targetUrl: string;
	mediaAssetId: string | null;
	windows: PromoPublicationWindowDto[];
	dailyStats: PromoDailyStatDto[];
}

/** Matches PromoAsset.MaxByteSize on the server. */
const MAX_MEDIA_BYTES = 512 * 1024;

/**
 * Editor for one promotional banner (card 34). Three sections: content, publication and
 * effectiveness. promotions.read opens it read-only; promotions.manage makes the fields editable;
 * publishing is a separate code again, so a marketing employee prepares the card end to end without
 * being the person who puts it on every customer's screen.
 */
export default class PromoBannerDetail extends BaseController {
	private withdrawDialog?: Dialog;
	private bannerId = "new";
	private pendingMediaBase64: string | null = null;
	private previewObjectUrl: string | null = null;

	public onInit(): void {
		this.getView()?.setModel(new JSONModel(this.emptyState()), "promo");
		this.getView()?.setModel(new JSONModel({ reason: "" }), "withdrawPromo");
		const route = this.getRouter().getRoute("promoBannerDetail");
		route?.attachPatternMatched((event) => {
			const args = event.getParameter("arguments") as { bannerId?: string };
			void this.onShow(args.bannerId ?? "new");
		});
	}

	public onExit(): void {
		this.releasePreview();
	}

	public onNavBack(): void {
		this.getRouter().navTo("promotions");
	}

	public async onFileSizeExceed(): Promise<void> {
		// FileUploader refuses the file itself; this only tells the editor why nothing happened.
		await this.showError("promotions.media.tooLarge");
	}

	public onFileSelected(event: FileUploader$ChangeEvent): void {
		// The UI5 typing declares the parameter as object[]; the runtime hands over real File
		// objects, which is what FileReader needs below.
		const file = event.getParameter("files")?.[0] as File | undefined;
		if (!file) {
			return;
		}

		// A courtesy to the editor, NOT a control: the real gate is the server-side magic-byte and
		// size validation, which stays exactly where it is.
		if (file.size > MAX_MEDIA_BYTES) {
			void this.showError("promotions.media.tooLarge");
			return;
		}

		void this.acceptFile(file);
	}

	public async onSave(): Promise<void> {
		const model = this.getView()?.getModel("promo") as JSONModel;
		const state = model.getData() as Record<string, unknown>;
		const bundle = await this.getResourceBundle();

		const body = {
			slug: state.slug,
			title: state.title,
			subtitle: this.blank(state.subtitle),
			category: this.blank(state.category),
			language: this.blank(state.language),
			mediaBase64: this.pendingMediaBase64,
			mediaUrl: this.pendingMediaBase64 ? null : this.blank(state.mediaUrl),
			// Keeps the stored picture when the editor changed only text. Dropped the moment a new
			// file is chosen or an external URL is typed instead.
			keepExistingAsset: !this.pendingMediaBase64 && !this.blank(state.mediaUrl),
			mediaAltText: state.mediaAltText,
			targetUrl: state.targetUrl,
			displayOrder: state.displayOrder,
			publishFrom: this.blank(state.publishFrom),
			publishUntil: this.blank(state.publishUntil)
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			if (this.bannerId === "new") {
				const created = await fetcher.json<string>("/api/v1/promo/banners", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(body)
				});
				this.bannerId = created;
				MessageToast.show(bundle.getText("promotions.detail.created") ?? "Created");
				this.getRouter().navTo("promoBannerDetail", { bannerId: created });
			} else {
				await fetcher.fetch(`/api/v1/promo/banners/${this.bannerId}`, {
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(body)
				});
				MessageToast.show(bundle.getText("promotions.detail.saved") ?? "Saved");
				await this.load();
			}
			this.pendingMediaBase64 = null;
		} catch (e) {
			this.reportFailure(e, bundle.getText("promotions.detail.saveFailure") ?? "Failed");
		}
	}

	public async onPublish(): Promise<void> {
		await this.post(`/api/v1/promo/banners/${this.bannerId}/publish`, "promotions.published");
	}

	public async onArchive(): Promise<void> {
		await this.post(`/api/v1/promo/banners/${this.bannerId}/archive`, "promotions.detail.archived");
	}

	public async onWithdraw(): Promise<void> {
		const state = this.getView()?.getModel("withdrawPromo") as JSONModel;
		state.setProperty("/reason", "");

		if (!this.withdrawDialog) {
			this.withdrawDialog = await Fragment.load({
				id: this.getView()?.getId(),
				name: "computec.b2b.portal.view.WithdrawPromoDialog",
				controller: this
			}) as Dialog;
			this.getView()?.addDependent(this.withdrawDialog);
		}
		this.withdrawDialog.open();
	}

	public async onWithdrawSubmit(): Promise<void> {
		const state = this.getView()?.getModel("withdrawPromo") as JSONModel;
		const reason = state.getProperty("/reason") as string;
		this.withdrawDialog?.close();
		await this.post(
			`/api/v1/promo/banners/${this.bannerId}/withdraw`, "promotions.withdrawn", { reason });
	}

	public onWithdrawCancel(): void {
		this.withdrawDialog?.close();
	}

	// -----------------------------------------------------------------------

	private async onShow(bannerId: string): Promise<void> {
		this.bannerId = bannerId;
		this.pendingMediaBase64 = null;
		this.releasePreview();

		const model = this.getView()?.getModel("promo") as JSONModel;
		if (bannerId === "new") {
			model.setData(this.emptyState());
			return;
		}
		await this.load();
	}

	private async load(): Promise<void> {
		const model = this.getView()?.getModel("promo") as JSONModel;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const detail = await fetcher.json<PromoBannerDetailDto>(
				`/api/v1/promo/banners/${this.bannerId}`);
			model.setData(this.toState(detail));
		} catch (e) {
			const bundle = await this.getResourceBundle();
			this.reportFailure(e, bundle.getText("promotions.loadFailure") ?? "Failed");
		}
	}

	private toState(detail: PromoBannerDetailDto): Record<string, unknown> {
		const summary = detail.summary;
		const ctr = summary.impressions > 0
			? `${((summary.clicks / summary.impressions) * 100).toFixed(1)}%`
			: "-";

		return {
			isNew: false,
			id: summary.id,
			slug: summary.slug,
			title: summary.title,
			subtitle: detail.subtitle ?? "",
			category: summary.category ?? "",
			language: summary.language ?? "",
			status: summary.status,
			visibleNow: summary.visibleNow,
			mediaUrl: detail.mediaAssetId ? "" : (summary.mediaUrl ?? ""),
			previewUrl: summary.mediaUrl ?? "",
			mediaAltText: detail.mediaAltText,
			targetUrl: detail.targetUrl,
			displayOrder: summary.displayOrder,
			publishFrom: summary.publishFrom ?? "",
			publishUntil: summary.publishUntil ?? "",
			impressions: summary.impressions,
			clicks: summary.clicks,
			ctr,
			distinctEnvironments: summary.distinctEnvironments,
			windows: detail.windows,
			dailyChart: PromoBannerDetail.toDailyChart(detail.dailyStats)
		};
	}

	/**
	 * Pivots the day/kind rows the API returns onto one row per day and scales both series to the
	 * same peak, so the bars are comparable across days and across kinds. Clicks are scaled to the
	 * impression peak on purpose: a click bar as long as the impression bar would suggest a 100%
	 * rate on a day with three impressions.
	 */
	private static toDailyChart(stats: PromoDailyStatDto[]): PromoDailyChartRow[] {
		const byDay = new Map<string, PromoDailyChartRow>();
		for (const stat of stats) {
			const row = byDay.get(stat.day) ?? {
				day: stat.day,
				impressions: 0,
				clicks: 0,
				impressionsPercent: 0,
				clicksPercent: 0,
				distinctEnvironments: 0
			};
			if (stat.kind === "Click") {
				row.clicks += stat.count;
			} else {
				row.impressions += stat.count;
			}
			row.distinctEnvironments = Math.max(row.distinctEnvironments, stat.distinctEnvironments);
			byDay.set(stat.day, row);
		}

		const rows = [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
		const peak = rows.reduce((max, row) => Math.max(max, row.impressions, row.clicks), 0);
		if (peak === 0) {
			return rows;
		}

		for (const row of rows) {
			row.impressionsPercent = (row.impressions / peak) * 100;
			row.clicksPercent = (row.clicks / peak) * 100;
		}
		return rows;
	}

	private emptyState(): Record<string, unknown> {
		return {
			isNew: true,
			id: "",
			slug: "",
			title: "",
			subtitle: "",
			category: "",
			language: "",
			status: "Draft",
			visibleNow: false,
			mediaUrl: "",
			previewUrl: "",
			mediaAltText: "",
			targetUrl: "",
			displayOrder: 0,
			publishFrom: "",
			publishUntil: "",
			impressions: 0,
			clicks: 0,
			ctr: "-",
			distinctEnvironments: 0,
			windows: [],
			dailyChart: []
		};
	}

	private async acceptFile(file: File): Promise<void> {
		try {
			this.pendingMediaBase64 = await this.readFileAsBase64(file);
			const model = this.getView()?.getModel("promo") as JSONModel;
			// Preview from the local file, so the crop is visible before anything is uploaded.
			this.releasePreview();
			this.previewObjectUrl = URL.createObjectURL(file);
			model.setProperty("/previewUrl", this.previewObjectUrl);
			model.setProperty("/mediaUrl", "");
		} catch {
			await this.showError("promotions.media.unreadable");
		}
	}

	/**
	 * A blob URL pins its file in memory until it is revoked. An editor trying ten pictures in one
	 * sitting used to hold all ten until the page reloaded.
	 */
	private releasePreview(): void {
		if (this.previewObjectUrl) {
			URL.revokeObjectURL(this.previewObjectUrl);
			this.previewObjectUrl = null;
		}
	}

	private readFileAsBase64(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			// readAsDataURL yields "data:image/png;base64,AAAA..." - the command wants the payload
			// only, so cut everything up to and including the comma.
			reader.onload = () => {
				const result = reader.result;
				resolve(typeof result === "string" ? (result.split(",", 2)[1] ?? "") : "");
			};
			reader.onerror = () => reject(new Error("file.read_failed"));
			reader.readAsDataURL(file);
		});
	}

	private async post(url: string, successKey: string, body?: object): Promise<void> {
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.fetch(url, body
				? {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(body)
				}
				: { method: "POST" });
			MessageToast.show(bundle.getText(successKey) ?? "Done");
			await this.load();
		} catch (e) {
			this.reportFailure(e, bundle.getText("promotions.actionFailure") ?? "Failed");
		}
	}

	private reportFailure(error: unknown, fallback: string): void {
		const err = error as ResponseError;
		MessageBox.error(problemMessage(err.bodyText, fallback));
	}

	private async showError(key: string): Promise<void> {
		const bundle = await this.getResourceBundle();
		MessageBox.error(bundle.getText(key) ?? key);
	}

	private blank(value: unknown): string | null {
		const text = typeof value === "string" ? value.trim() : "";
		return text.length > 0 ? text : null;
	}
}
